import PropTypes from "prop-types";
import { useCallback } from "react";

import ActionButton from "../../formInputs/buttons/ActionButton";
import Icon from "../../icon/Icon";

import "./StudentInfoHeader.scss";

const StudentInfoHeader = ({
    basePath,
    buildStudentUrl,
    canEdit,
    currentStudentIndex = -1,
    currentTimePeriodDto,
    currentTimePeriodName,
    downloadPdf,
    gradingProfileStatus,
    groupLabel,
    imageEl,
    incomingTemplateId,
    isOwned = false,
    loader,
    location,
    navigate,
    navState,
    schoolYearName,
    selectedEpr,
    setPreviewShow,
    setSemester1ViewShow,
    student,
    studentProfileData,
    students = [],
    view
}) => {
    const { pdfProcessed } = studentProfileData || {};
    const isSecondSemester = currentTimePeriodDto?.name === "2nd Semester";

    const total = Array.isArray(students) ? students.length : 0;
    const atStart = currentStudentIndex <= 0;
    const atEnd = total === 0 || currentStudentIndex >= total - 1;

    const goToStudentNumber = useCallback(
        (nextStudentNumber) => {
            if (!nextStudentNumber) return;
            navigate(buildStudentUrl(nextStudentNumber), { state: location.state });
        },
        [location.state, navigate, buildStudentUrl]
    );

    const onSelectStudent = useCallback(
        (nextStudentNumber) => goToStudentNumber(nextStudentNumber),
        [goToStudentNumber]
    );

    const onPrevStudent = useCallback(() => {
        if (currentStudentIndex <= 0) return;
        const prev = students[currentStudentIndex - 1];
        goToStudentNumber(prev?.studentNumber);
    }, [currentStudentIndex, students, goToStudentNumber]);

    const onNextStudent = useCallback(() => {
        if (currentStudentIndex < 0) return;
        const next = students[currentStudentIndex + 1];
        if (!next) return;
        goToStudentNumber(next.studentNumber);
    }, [currentStudentIndex, students, goToStudentNumber]);

    const handleBackToGroups = useCallback(() => {
        const subjectId = navState?.selectedTemplateId ?? incomingTemplateId ?? null;
        const sleSelected = navState?.from === "GROUPS_SLE";
        const preselectedGroupIds = navState?.preselectedGroupIds ?? [];
        const currentGradeName = studentProfileData?.gradeName;
        const base = basePath || "/home";

        navigate(`${base}/summary/input-ratings/groups`, {
            state: {
                subjectId,
                sleSelected: subjectId === "SLE" || sleSelected,
                preselectedGroupIds,
                selectedEpr: selectedEpr ?? navState?.selectedEpr ?? null,
                selectedGrade: currentGradeName || navState?.selectedGrade || null,
                selectedSchoolLocationKey: navState?.selectedSchoolLocationKey ?? null
            }
        });
    }, [basePath, incomingTemplateId, navState, selectedEpr, studentProfileData?.gradeName, navigate]);

    const handleBackToHomerooms = useCallback(() => {
        const subjectId = navState?.selectedTemplateId ?? incomingTemplateId ?? null;
        const sleSelected = navState?.from === "SLE";
        const currentGradeName = studentProfileData?.gradeName;
        const base = basePath || "/home";
        navigate(`${base}/summary/input-ratings`, {
            state: {
                subjectId,
                sleSelected: subjectId === "SLE" || sleSelected,
                selectedEpr: selectedEpr ?? navState?.selectedEpr ?? null,
                selectedGrade: currentGradeName || navState?.selectedGrade || null,
                selectedSchoolLocationKey: navState?.selectedSchoolLocationKey ?? null
            }
        });
    }, [basePath, incomingTemplateId, navState, selectedEpr, studentProfileData?.gradeName, navigate]);

    return (
        <>
            <div className="right-page-title">
                <h4>Input Ratings</h4>
                <h5>
                    <span className="student-info-header__time-period-text">{currentTimePeriodName}</span> |{" "}
                    {schoolYearName}
                </h5>
            </div>
            <hr />
            {student && students.length > 1 && (
                <>
                    <div className="student-info-header__group-container">
                        {view === "GROUPS" && groupLabel && (
                            <h5 className="student-info-header__group-title">
                                <b>Group:</b> {groupLabel}
                            </h5>
                        )}
                        <div className="student-switcher">
                            <div className="student-switcher__row">
                                <div className="student-info-header__student-selector">
                                    <label
                                        htmlFor="student-switcher-select"
                                        className="student-info-header__student-label"
                                    >
                                        <b>Select Student:</b>
                                        <select
                                            id="student-switcher-select"
                                            className="student-switcher__select student-info-header__student-select"
                                            value={student?.studentNumber ?? ""}
                                            onChange={(e) => onSelectStudent?.(e.target.value)}
                                        >
                                            {students.map((s) => (
                                                <option
                                                    key={s.gradingProfileStudentId ?? s.studentNumber}
                                                    value={s.studentNumber}
                                                >
                                                    {s.lastName}, {s.firstName}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <ActionButton
                                        label="Prev"
                                        disable={atStart}
                                        onClick={onPrevStudent}
                                        aria-label="Previous student"
                                    />

                                    <ActionButton
                                        label="Next"
                                        disable={atEnd}
                                        onClick={onNextStudent}
                                        aria-label="Next student"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <hr />
                </>
            )}
            <div className="student-info-header__main-content">
                <div className="student-info-header__student-info">
                    {isOwned && isSecondSemester && setSemester1ViewShow && (
                        <div className="student-info-header__semester1-button">
                            <ActionButton
                                className="action-button-200"
                                label="View 1st Semester Report Card"
                                onClick={() => setSemester1ViewShow(true)}
                            />
                        </div>
                    )}
                    {imageEl || <Icon iconName="NO_STUDENT_IMAGE" fill="#19608F" height={120} width={90} />}
                    <div>
                        <div className="student-info-header__student-name">
                            {student?.firstName} {student?.lastName}
                        </div>
                        <div className="student-info-header__student-number">Student #{student?.studentNumber}</div>
                        {!canEdit && <div className="view-only-badge">View Only</div>}
                    </div>
                </div>

                <div className="student-info-header__actions">
                    <div className="student-info-header__action-item">
                        <ActionButton
                            className="action-button-200"
                            label={view === "GROUPS" ? "Back to Group Ratings" : "Back to Homeroom"}
                            onClick={() => {
                                if (view === "GROUPS") {
                                    handleBackToGroups();
                                } else {
                                    handleBackToHomerooms();
                                }
                            }}
                        />
                    </div>
                    {isOwned && (
                        <div className="student-info-header__action-item">
                            <ActionButton
                                className="action-button-200"
                                label="Preview Parent View"
                                onClick={() => setPreviewShow(true)}
                            />
                        </div>
                    )}

                    <div className="student-info-header__pdf-container">
                        <ActionButton
                            className="action-button-200"
                            disable={loader || gradingProfileStatus !== "READY_FOR_RELEASE" || pdfProcessed !== "Y"}
                            label="Download PDF"
                            onClick={() => downloadPdf()}
                        />

                        {(gradingProfileStatus !== "READY_FOR_RELEASE" || pdfProcessed !== "Y") && (
                            <p className="student-info-header__pdf-note">
                                Note: PDF may take up to 24 hours to become available for download
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

StudentInfoHeader.propTypes = {
    basePath: PropTypes.string,
    buildStudentUrl: PropTypes.func.isRequired,
    canEdit: PropTypes.bool,
    currentStudentIndex: PropTypes.number,
    currentTimePeriodDto: PropTypes.object,
    currentTimePeriodName: PropTypes.string,
    downloadPdf: PropTypes.func,
    gradingProfileStatus: PropTypes.string,
    groupLabel: PropTypes.string,
    imageEl: PropTypes.node,
    incomingTemplateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isOwned: PropTypes.bool,
    loader: PropTypes.bool,
    location: PropTypes.object.isRequired,
    navigate: PropTypes.func.isRequired,
    navState: PropTypes.object,
    schoolYearName: PropTypes.string,
    selectedEpr: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.oneOf([null])]),
    setPreviewShow: PropTypes.func,
    setSemester1ViewShow: PropTypes.func,
    student: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        studentNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    }),
    studentProfileData: PropTypes.object,
    students: PropTypes.array,
    view: PropTypes.oneOf(["GROUPS", "HOMEROOM"])
};

export default StudentInfoHeader;
